<template>
  <main-section>
    <loader v-if="isLoading" />
    <div v-else class="h-full">
      <table-action-header :title="$t('Sidebar.Statistics')" />
      <div class="flex h-[43px] gap-4">
        <v-select
          class="w-56"
          v-model="selectedFilterOption"
          :options="filterOptions"
          :reduce="(option : any) => option.key"
          label="label"
          :clearable="false"
          :searchable="false"
        />
        <dx-date-box
          v-model="from"
          :height="43"
          :show-clear-button="true"
          :display-format="$t('LocaleFormats.formatDateBox')"
          type="datetime"
          :max="to"
        />
        <dx-date-box
          v-model="to"
          :height="43"
          :show-clear-button="true"
          :display-format="$t('LocaleFormats.formatDateBox')"
          type="datetime"
          :min="from"
          :max="maxTo"
        />
        <the-button
          :icon="['fas', 'rotate-left']"
          button-type="primaryButton"
          @execute-method="buttonPress"
        />
      </div>
      <div class="relative">
        <div class="mb-8 mt-8 border">
          <apexchart
            :key="chartSeriesSessionLength.length"
            type="bar"
            :options="chartOptionsSessionLength"
            :series="chartSeriesSessionLength"
            height="500"
          />
        </div>
        <div class="mb-8 border">
          <apexchart
            :key="chartSeriesBar.length"
            type="bar"
            :options="chartOptionsBar"
            :series="chartSeriesBar"
            height="500"
          />
        </div>
        <div class="mb-8 border">
          <apexchart
            :key="chartSeriesLoad.length"
            type="bar"
            :options="chartOptionsLoad"
            :series="chartSeriesLoad"
            height="500"
          />
        </div>
        <br />
        <div
          v-if="isLoadingGraphs"
          class="absolute inset-0 z-[100000] flex justify-center bg-[rgba(255,255,255,0.5)]"
        >
          <div class="mt-56">
            <small-loader height="h-16" width="w-16" />
          </div>
        </div>
      </div>
    </div>
  </main-section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainSection from "@/core/Components/MainSection.vue";
import Loader from "@/core/Components/Loader.vue";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import DxDateBox from "devextreme-vue/date-box";
import TheButton from "@/core/Components/TheButton.vue";
import VSelect from "vue-select";
import moment from "moment";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import { useUserStore } from "@/core/Store/userStore";
import { useChangeCompanyStore } from "@/core/Store/changeCompanyStore";
import { useParkingSpaceFilterStore } from "@/core/Store/useParkingSpaceFilterStore";
import StatisticsService from "@/modules/Parkom/Submodules/Statistics/Services/StatisticsService";
import { StatisticsData } from "../Services/StatisticsTypes";
import { ApexOptions } from "apexcharts";
import UserCustomisableGraphService from "@/modules/MapView/Services/UserCustomisableGraphService";
import SmallLoader from "@/core/Components/SmallLoader.vue";

export default defineComponent({
  components: {
    MainSection,
    Loader,
    VSelect,
    TheButton,
    DxDateBox,
    TableActionHeader,
    SmallLoader,
  },
  data() {
    return {
      isLoading: false,
      isLoadingGraphs: false,
      selectedFilterOption: "today",
      filterOptions: [
        { key: "custom" },
        { key: "today" },
        { key: "last24hours" },
        { key: "yesterday" },
        { key: "thisWeek" },
        { key: "lastWeek" },
        { key: "thisMonth" },
        { key: "lastMonth" },
      ],
      from: moment().startOf("day").format("YYYY-MM-DDTHH:mm"),
      to: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
      toast: useToast(),
      userStore: useUserStore(),
      changeCompanyStore: useChangeCompanyStore(),
      parkingSpaceFilterStore: useParkingSpaceFilterStore(),
      statisticsService: new StatisticsService(),
      statisticsData: {} as StatisticsData,
      chartSeriesBar: [] as ApexAxisChartSeries,
      chartOptionsBar: {} as ApexOptions,
      chartSeriesLoad: [] as ApexAxisChartSeries,
      chartOptionsLoad: {} as ApexOptions,
      chartSeriesSessionLength: [] as ApexAxisChartSeries,
      chartOptionsSessionLength: {} as ApexOptions,
      graphService: new UserCustomisableGraphService(),
    };
  },
  computed: {
    maxTo() {
      return this.from
        ? moment(this.from).add(1, "month").format("YYYY-MM-DDTHH:mm")
        : null;
    },
  },
  async created() {
    this.isLoading = true;
    await this.getStatistics();

    this.createEnterExitGraph();
    this.createLoadGraph();
    this.createSessionLengthGraph();

    this.translateFilterOptions();
    this.isLoading = false;
  },
  watch: {
    async selectedFilterOption() {
      if (this.selectedFilterOption != "custom") {
        switch (this.selectedFilterOption) {
          case "today":
            this.from = moment().startOf("day").format("YYYY-MM-DDTHH:mm");
            this.to = moment().endOf("day").format("YYYY-MM-DDTHH:mm");
            break;
          case "last24hours":
            this.from = moment()
              .subtract(24, "hours")
              .format("YYYY-MM-DDTHH:mm");
            this.to = moment().format("YYYY-MM-DDTHH:mm");
            break;
          case "yesterday":
            this.from = moment()
              .subtract(1, "day")
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm");
            this.to = moment()
              .subtract(1, "day")
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm");
            break;
          case "thisWeek":
            this.from = moment().startOf("isoWeek").format("YYYY-MM-DDTHH:mm");
            this.to = moment().format("YYYY-MM-DDTHH:mm");
            break;
          case "lastWeek":
            this.from = moment()
              .subtract(1, "week")
              .startOf("isoWeek")
              .format("YYYY-MM-DDTHH:mm");
            this.to = moment()
              .subtract(1, "week")
              .endOf("isoWeek")
              .format("YYYY-MM-DDTHH:mm");
            break;
          case "thisMonth":
            this.from = moment().startOf("month").format("YYYY-MM-DDTHH:mm");
            this.to = moment().format("YYYY-MM-DDTHH:mm");
            break;
          case "lastMonth":
            this.from = moment()
              .subtract(1, "month")
              .startOf("month")
              .format("YYYY-MM-DDTHH:mm");
            this.to = moment()
              .subtract(1, "month")
              .endOf("month")
              .format("YYYY-MM-DDTHH:mm");
            break;
        }
        this.updateGraphs();
      }
    },
    async "changeCompanyStore.companyId"() {
      this.updateGraphs();
    },
    async "parkingSpaceFilterStore.selectedParkingSpaceIds"() {
      this.updateGraphs();
    },
    async "userStore.languageCode"() {
      this.translateFilterOptions();
    },
  },
  methods: {
    async getStatistics() {
      this.isLoadingGraphs = true;
      try {
        this.statisticsData = await this.statisticsService.getStatistics({
          from: moment(this.from).utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          to: moment(this.to).utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          parkingSpaceIds: this.parkingSpaceFilterStore.selectedParkingSpaceIds,
          clientTimeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isLoadingGraphs = false;
      }
    },
    async buttonPress() {
      this.selectedFilterOption = "custom";
      const monthsDifference = moment(this.to).diff(
        moment(this.from),
        "months",
        true
      );
      if (monthsDifference >= 1) {
        this.toast.warning(
          (this as any).$t("Statistics.DateDifferenceWarning")
        );
      } else {
        this.updateGraphs();
      }
    },
    translateFilterOptions() {
      this.filterOptions = this.filterOptions.map((option) => ({
        ...option,
        label: (this as any).$t(`Statistics.Filters.${option.key}`),
      }));
    },
    getMaxOccupationsByHour() {
      let maxY = 0;
      this.statisticsData.occupationsByHour.forEach((hour) => {
        maxY = Math.max(maxY, hour.enters + hour.exits);
      });
      return maxY;
    },
    getMaxSessionLength() {
      let maxY = 0;
      this.statisticsData.averageSpaceSessions.forEach((space) => {
        maxY = Math.max(maxY, space.averageSession);
      });
      return maxY;
    },
    createEnterExitGraph() {
      const maxY = this.getMaxOccupationsByHour();
      const { series, options } = this.graphService.generateGraphSeriesOptions(
        {
          yPropertyConfig: [
            {
              id: "enters",
              visible: true,
              opposite: false,
              title: `${(this as any).$t("Dashboard.Enters")} - ${(
                this as any
              ).$t("Dashboard.Exits")}`,
              min: 0,
              max: maxY,
            },
            {
              id: "exits",
              visible: false,
              opposite: false,
              title: `${(this as any).$t("Dashboard.Enters")} - ${(
                this as any
              ).$t("Dashboard.Exits")}`,
              min: 0,
              max: maxY,
            },
          ],
          xPropertyName: "calculatedDate",
          xaxisType: "datetime",
          graphType: "bar",
          seriesNames: [
            (this as any).$t("Dashboard.Enters"),
            (this as any).$t("Dashboard.Exits"),
          ],
          graphTitle: (this as any).$t("Statistics.ParkingFrequency"),
          types: ["bar", "bar"],
          colors: ["#d4515c", "#308e1f"],
          dateFormat: "date",
        },
        this.statisticsData.occupationsByHour
      );
      this.chartSeriesBar = series;
      this.chartOptionsBar = options;
    },
    createLoadGraph() {
      const { series, options } = this.graphService.generateGraphSeriesOptions(
        {
          yPropertyConfig: [
            {
              id: "load",
              visible: true,
              opposite: false,
              title: (this as any).$t("Dashboard.Load") + " %",
              min: 0,
              max: 100,
              unit: "%",
            },
          ],
          xPropertyName: "calculatedDate",
          xaxisType: "datetime",
          graphType: "bar",
          seriesNames: [(this as any).$t("Dashboard.Load")],
          graphTitle: (this as any).$t("Dashboard.OccupationByHour"),
          types: ["bar"],
          colors: ["#00ced1"],
          dateFormat: "date",
        },
        this.statisticsData.occupationsByHour
      );
      this.chartSeriesLoad = series;
      this.chartOptionsLoad = options;
    },
    createSessionLengthGraph() {
      const { series, options } = this.graphService.generateGraphSeriesOptions(
        {
          yPropertyConfig: [
            {
              id: "averageSession",
              visible: true,
              opposite: false,
              title: (this as any).$t("Statistics.SessionLength") + " (min)",
              min: 0,
              max: this.getMaxSessionLength(),
              unit: "min",
            },
          ],
          xPropertyName: "spaceName",
          xaxisType: "category",
          graphType: "bar",
          seriesNames: [(this as any).$t("Statistics.SessionLength")],
          graphTitle: (this as any).$t("Statistics.AverageParkingSession"),
          types: ["bar"],
          colors: ["#007cc1"],
          dateFormat: "none",
          dataLabels: true,
        },
        this.statisticsData.averageSpaceSessions
      );
      this.chartSeriesSessionLength = series;
      this.chartOptionsSessionLength = options;
    },
    async updateGraphs() {
      await this.getStatistics();

      this.chartSeriesSessionLength[0].data =
        this.statisticsData.averageSpaceSessions.map((space) => ({
          x: space.spaceName,
          y: space.averageSession,
        }));
      if (
        this.chartOptionsSessionLength &&
        Array.isArray(this.chartOptionsSessionLength.yaxis)
      ) {
        const maxY = this.getMaxSessionLength();
        this.chartOptionsSessionLength = {
          ...this.chartOptionsSessionLength,
          yaxis: [
            {
              ...this.chartOptionsSessionLength.yaxis[0],
              max: maxY,
            },
          ],
        };
      }

      this.chartSeriesBar[0].data = this.statisticsData.occupationsByHour.map(
        (hour) => ({
          x: hour.calculatedDate,
          y: hour.enters,
        })
      );
      this.chartSeriesBar[1].data = this.statisticsData.occupationsByHour.map(
        (hour) => ({
          x: hour.calculatedDate,
          y: hour.exits,
        })
      );
      if (this.chartOptionsBar && Array.isArray(this.chartOptionsBar.yaxis)) {
        const maxY = this.getMaxOccupationsByHour();
        this.chartOptionsBar = {
          ...this.chartOptionsBar,
          yaxis: [
            {
              ...this.chartOptionsBar.yaxis[0],
              max: maxY,
            },
            {
              ...this.chartOptionsBar.yaxis[1],
              max: maxY,
            },
          ],
        };
      }

      this.chartSeriesLoad[0].data = this.statisticsData.occupationsByHour.map(
        (hour) => ({
          x: hour.calculatedDate,
          y: hour.load,
        })
      );
    },
  },
});
</script>

<style scoped></style>
